import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase, SortDirection, SortEntry, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { AuthService, IAuthSearchFilters } from 'app/um/services/auth-service';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation, ConfirmationOptions } from 'app/shared/elements/ess-confirm';
import { QueueUserTracker } from 'app/um/components/tracking/queue-user-tracker';
import { ITrackAuth } from 'app/um/services/tracking-service';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { MemberEligibility } from 'app/cd/models/essette-api';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { IProviderSearchFilters } from 'app/cd/services/provider-service';
import * as UM from 'app/um/models/essette-api';
import { requiredFields as memberRequiredFields, requiredPlanFields as planRequiredFields } from 'app/cd/elements/members/member-display';

@autoinject()
export class AuthQueue extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected authQueueId: string | null = null;
    // To load necessary auth queue information
    protected authQueue: UM.AuthQueue | null = null;
    // Queue Auths to Display on Grid
    protected auths: ITrackAuth[] = [];

    protected authSearchFilters: IAuthSearchFilters = {};
    protected providerSearchFilters: IProviderSearchFilters = {};
    protected authAvailableFilters = [
        'mine', 'openOnly', 'closedOnly', 'overDue', 'number', 'type', 'class', 'class.subclass', 'status', 'status.reason', 'assignedTo', 'requestedDateTime', 'dueDate', 'nextReviewDate'
    ];
    protected providerAvailableFilters = [
        'identifier', 'npi', 'providerFirstName', 'providerLastName', 'specialty', 'gender', 'facilityName', 'groupName', 'type', 'street1', 'city', 'region', 'postalCode'
    ];

    // Fields for Tracking Users in Auths
    protected trackingQueue: UM.AuthQueue | null = null;
    @observable
    protected trackedAuths: ITrackAuth[] | null = null;

    private sorting: Sorting = new Sorting();
    private defaultSorting: Sorting = new Sorting();
    private defaultSortEntry?: SortEntry;
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    protected hasAuthRoute: boolean = false;

    protected showAuthSummaryLink: boolean = false;
    protected showProviderSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected openProviderSummary = openProviderSummary;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $queueUserTracker!: QueueUserTracker;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(params: { queueId: string }): Promise<boolean> {
        await this.accountContext.load();

        if(!params.queueId) { throw new Error('Missing required parameters: queueId'); }

        const authQueueRouteCode: string = params.queueId ? `um.queues.auth.${params.queueId}` : '';
        if(!this.accountContext.hasRouteAccess(authQueueRouteCode)) { return false; }

        return true;
    }

    protected async activate(params: { queueId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(!params?.queueId) {
            throw new Error('Missing required parameters: queueId');
        }

        await Promise.all([
            this.checkPermissions(),
            this.loadSettings()
        ]);

        this.authQueueId = params.queueId;
        await this.reload();

        await this.loadAuthQueueInformation();
        if(this.authQueue && routeConfig.navModel) {
            const title = this.i18n.tr('um:routes.queue.auth.$title', { what: this.authQueue.$display });
            routeConfig.navModel.setTitle(title);
        }
    }

    protected attached(): void {
        this.$queueUserTracker.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthsByQueue();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;

        // For other fields, apply API sorting
        if(direction === null) {
            this.sorting.remove(property);
        } else {
            this.sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    // New Tracked Auth changes, set the auth.users to the tracked auth users for the user-tracker icons.
    protected async trackedAuthsChanged(newValue: ITrackAuth[], oldValue: ITrackAuth[]): Promise<void> {
        if(newValue) {
            const trackedAuths = (newValue && newValue.length > 0) ? newValue : [];

            this.auths.forEach(a => {
                const trackedAuth = trackedAuths.find(ta => ta.id === a.id);
                if(trackedAuth) {
                    a.users = trackedAuth.users;
                }
                else {
                    a.users = [];
                }
            });
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    // Return Auth, confirm with user
    protected async returnAuth(auth: UM.Auth): Promise<void> {
        if(auth.id) {
            const title = this.i18n.tr('ui:actions.return', { what: this.i18n.tr('um:auth.$') });
            const message = this.i18n.tr('um:local.auth.returnAuth', { what: auth.number });
            const result = await confirmation.showCustom(title, message, ConfirmationOptions.OKCancel)
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
            if(!(result.dialogResponse === confirmation.options.OK)) { return; }
            await this.returnFromQueue(auth.id);
        }
    }

    protected filterAuths(): void {
        this.loadAuthsByQueue();
    }

    protected clearResults(): void {
        this.authSearchFilters = {};
        this.providerSearchFilters = {};
        this.loadAuthsByQueue();
    }

    // Build track users queue to pass to queue-user-tracker
    private buildTrackUsersQueue(): void {
        if(this.authQueueId) {
            this.trackingQueue = {
                id: this.authQueueId,
                auths: this.auths
            }
        }
        else {
            this.trackingQueue = null
        }
    }

    private getHealthPlan = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.healthPlan?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    private getLineOfBusiness = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.lineOfBusiness?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    private getProductList = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthsByQueue(): Promise<void> {
        if(!this.authQueueId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true,
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting
        };

        const includes = [
            'assignedTo',
            'class',
            'class.subClass',
            'healthCareServiceType',
            'member',
            'member.plan',
            'member.plan.eligibility.history.product',
            'member.plan.eligibility.history.product.healthPlan',
            'member.plan.eligibility.history.product.lineOfBusiness',
            'placeOfService',
            'priority',
            'provider.attending',
            'provider.requested',
            'provider.requesting',
            'provider.servicing',
            'status',
            'type'
        ];

        const fields = [
            'admission.date',
            'assignedTo.description',
            'class.description',
            'class.subClass.description',
            'detail.metadata.started.timestamp',
            'discharge.date',
            'dueDateTime',
            'hasAuthRoute',
            'healthCareServiceType.description',
            'isAdmission',
            'isClosed',
            'member.*',
            'nextReviewDate',
            'number',
            'placeOfService.description',
            'provider.attending.description',
            'provider.attending.name.full',
            'provider.requested.description',
            'provider.requested.name.full',
            'provider.requesting.description',
            'provider.requesting.name.full',
            'provider.servicing.description',
            'provider.servicing.name.full',
            'priority.description',
            'requestedDateTime',
            'status.description',
            'type.description',
            'users'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);
        planRequiredFields.forEach(field => !fields.includes(`member.plan.${field}`) ? fields.push(`member.plan.${field}`) : null);

        try {
            this.state.beginLoad();

            // too many filters can exceed URL character limit, so use filters only to fetch IDs first
            const idResponse = await this.authService.searchAuthQueues(this.authQueueId, this.authSearchFilters, this.providerSearchFilters, options);
            const authIds: string[] = idResponse.results.map(auth => auth.id!);
            // then use those IDs to fetch the rest of the includes and fields
            const fullResponse = await this.authService.loadAuthsByIdFromAuthQueue(this.authQueueId, authIds, { includeInactive: true, sorting: options.sorting }, includes, fields);
            this.auths = fullResponse.results;
            this.resultCount = idResponse.count || 0;
            this.buildTrackUsersQueue();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAuthQueueInformation(): Promise<void> {
        if(!this.authQueueId) return;
        try {
            this.state.beginLoad();

            const response = await this.authService.loadQueue(this.authQueueId);
            this.authQueue = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async returnFromQueue(authId: string): Promise<void> {
        if(!this.authQueueId) return;
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.returnFromQueue(authId, this.authQueueId);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            this.logger.error(`Unable to return auth from queue`, error);
        } finally {
            this.toaster.standard.returned('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.authService.loadAuthSettings();
            const settings = response.settings;

            // attempt to configure default sort via attribute
            const defaultSort = getApiSetting(settings, 'defaultAuthQueueSort.$default')?.toString();
            if(!defaultSort) return; // no attribute value found

            const [reference, directionString]: string[] = defaultSort.split(':');
            if(!reference || !['asc', 'desc'].includes(directionString)) return; // attribute value is invalid

            this.defaultSortEntry = {
                reference,
                direction: directionString === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            };
        } catch(error) {
            throw error;
        } finally {
            // if default sort not configured via attribute, use fallback
            if(!this.defaultSortEntry) {
                this.defaultSortEntry = {
                    reference: 'dueDateTime',
                    direction: SortDirection.Ascending
                };
            }
            this.defaultSorting.entries.push(this.defaultSortEntry);
            this.state.endLoad();
        }
    }
}
